<template>
  <div class="d-inline-flex align-middle addobject-btn">
    <a class="btn btn-themed d-flex align-items-center gap-2"
       :class="btnclass"
       v-if="showAddBtn"
       @click="show">
      <template v-if="svgPassed">
        <component :is="svgPassed"
                   :class="svgClass"/>
      </template>
      <template v-else>
        <component :is="svgComponent"
                   :class="svgClass"/>
      </template>
      <span>{{ $t('add') }}</span>
    </a>
  </div>
</template>

<script setup>
import BoxSvg from '@/assets/svg-vue/sidebar/box.svg'
import PatientsSvg from '@/assets/svg-vue/sidebar/patients.svg'
import TemplatesSvg from '@/assets/svg-vue/sidebar/template.svg'
import CalculatorSvg from '@/assets/svg-vue/sidebar/calculator.svg'
import ShieldSvg from '@/assets/svg-vue/sidebar/shield.svg'
import FolderSvg from '@/assets/svg-vue/sidebar/folder.svg'
import CRMSvg from '@/assets/svg-vue/sidebar/crm.svg'
import MarketingSvg from '@/assets/svg-vue/sidebar/marketing.svg'
import ClinicSvg from '@/assets/svg-vue/sidebar/clinic.svg'
import LabSvg from '@/assets/svg-vue/sidebar/lab.svg'
import UserSvg from '@/assets/svg-vue/header/adduser_t.svg'
import PlusSvg from '@/assets/svg-vue/header/plus.svg'
import { useStore} from "vuex"
import moment from "moment/moment"
import {copyObject} from "@/extensions/prototypes/prototypes"
import { computed, onBeforeMount, getCurrentInstance} from "vue"

const props = defineProps({
  module: {
    type: String,
  },
  btnclass: {
    type: String,
  },
  svgPassed: {
    type: String,
    default: ''
  },
  svgClass: {
    type: String,
    default: 'svg-white'
  }
})

const store = useStore()
let i18n = null

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
})

const show = () => {
  store.commit('setModule', props.module)
  store.commit('setSelectedModel', copyObject(newObject.value))
  store.commit('setShowModal', true)
}
const model = computed(() => store.state.datatable.viewModel)
const options = computed(() => store.state.datatable.eloquentOptions)
const document = computed(() => store.state.warehouseoperation.operation)
const branch = computed(() => store.state.auth.branch)
const branches = computed(() => store.state.auth.branches)
const rights = computed(() => store.state.auth.rights)
const accountingmonth = computed(() => store.state.accounting.accountingmonth)
const clinic = computed(() => store.state.auth.clinic)
const prices = computed(() => store.state.auth.prices.filter(p => p.active))
const user = computed(() => store.state.auth.user)
const patient = computed(() => store.state.patient.patient)

const showAddBtn = computed(() => {
  if(!options.value?.actions?.includes('add')) return false
  if(document.value) return false
  if(options.value?.rights?.add && !rights.value.includes(options.value?.rights?.add)) return false
  if(options.value?.addCondition && !options.value?.addCondition(store)) return false
  return true
})

const newObject = computed(() => {
  let obj = {}
  if(props.module === 'patients' && ['scan', 'client_document', 'client_note'].includes(model.value)) {
    if(patient.value) obj.client_id = patient.value.id
  }
  if(props.module === 'patients' && ['insurance_polis', 'report_populated', 'ai_photo_study'].includes(model.value)) {
    if(patient.value) obj.patient_id = patient.value.id
  }
  if(props.module === 'patients' && ['patient'].includes(model.value)) {
    obj.branch_id = branch.value
    obj.send_marketing = true
    obj.send_notifications = true
    obj.document_type_id = 1
    if(rights.value.includes('bonuses')
        && store.getters.getClinicAdditional('bonuses_for_new_patients')
        && store.getters.getClinicAdditional('bonuses_enabled')) {
      obj.use_bonuses = true
    } else {
      obj.use_bonuses = false
    }
  }
  if(props.module === 'patients' && ['task'].includes(model.value)) {
    obj.patient_id = patient.value?.id || null
    obj.patient = patient.value || null
    obj.phone = patient.value?.phone || null
    obj.task_type = 'task'
  }
  if(props.module === 'patients' && ['waiting_list'].includes(model.value)) {
    obj.interval_from = moment().format('YYYY-MM-DD')
    obj.interval_to = moment().format('YYYY-MM-DD')
  }
  if(props.module === 'patients' && ['feedback'].includes(model.value)) {
    obj.author_id = patient.value || null
  }
  if(props.module === 'services' && ['service'].includes(model.value)) {
    obj.visible = 1
    obj.price_id = prices.value.length ? prices.value[0].id : null
    obj.ndfl_code = '01'
  }
  if(props.module === 'settings' && ['notification_setting'].includes(model.value)) {
    obj.notification_channels = []
  }
  if(props.module === 'marketing' && ['notification_setting'].includes(model.value)) {
    obj.notification_channels = []
  }
  if(props.module === 'settings' && ['pay_method', 'client_source'].includes(model.value)) {
    if(clinic.value) obj.clinic_id = clinic.value.id
  }
  if(props.module === 'settings' && ['pay_method'].includes(model.value)) {
    obj.branches = copyObject(branches.value)
  }
  if(props.module === 'staff' && ['doctor', 'technic', 'administrator', 'assistant', 'chair', 'curator'].includes(model.value)) {
    obj.branch_id = branch.value
  }
  if(props.module === 'staff' && ['doctor', 'technic', 'administrator', 'assistant', 'curator'].includes(model.value)) {
    obj.percent = 0
    obj.rate = 0
  }
  if(props.module === 'staff' && ['custom_role'].includes(model.value)) {
    obj.functionIds = []
  }
  if(props.module === 'superadmin') {
    if(clinic.value) obj.clinic_id = clinic.value.id
    if(['service'].includes(model.value)) {
      obj.visible = 1
    }
    if(['billing_payment'].includes(model.value)) {
      obj.account_manager_id = user.value?.id || null
      obj.sum = 0
      obj.months = 1
    }
    if(['clinic'].includes(model.value)) {
      obj.source = 4 // dentist-plus.com
      obj.type = 2 // абонентская плата
      obj.tariff_id = 14 // demo
    }
  }
  if(props.module === 'warehouse' && ['materialstock'].includes(model.value)) {
    obj.min_stock = 0;
  }
  if(props.module === 'staff' && ['technic'].includes(model.value)) {
    obj.profession_string = i18n.t('technic')
  }
  if(props.module === 'staff' && ['assistant'].includes(model.value)) {
    obj.profession_string = i18n.t('assistant')
  }
  if(['accounting', 'expenses'].includes(props.module) && ['other_income', 'other_expense'].includes(model.value)) {
    if(accountingmonth.value) obj.bookkeeping_id = accountingmonth.value.id
    obj.date = moment().format('YYYY-MM-DD')
  }
  if(props.module === 'templates' && ['full_treatment_template'].includes(model.value)) {
    obj.options = []
  }
  if(['document_category'].includes(model.value)) {
    obj.sort_order = 1
    obj.active = 1
  }
  if(props.module === 'crm' && ['task'].includes(model.value)) {
    obj.task_type = 'task'
    obj.start = moment().format('YYYY-MM-DD')
    obj.deadline = moment().format('YYYY-MM-DD')
  }
  if(props.module === 'crm' && ['lead'].includes(model.value)) {
    obj.status = 'new'
    obj.source_id = 0
    obj.source = i18n.t('manual')
    if(store.getters.dynamicConfigValue('manual_responsible_id')) {
      obj.responsible_user_id = store.getters.dynamicConfigValue('manual_responsible_id')
    }
  }
  if(props.module === 'superadmin' && ['popup'].includes(model.value)) {
    obj.roles = []
  }
  if(props.module === 'marketing' && ['marketing_mailing'].includes(model.value)) {
    obj.notification_channel_id = 1
  }
  if(props.module === 'services' && ['price'].includes(model.value)) {
    obj.active = 1
  }
  if(props.module === 'laboratories' && ['laboratory_service'].includes(model.value)) {
    obj.active = 1
  }
  return obj
})

const svgComponent = computed(() => {
  const arr = {
    'warehouse': BoxSvg,
    'patients': PatientsSvg,
    'services': FolderSvg,
    'templates': TemplatesSvg,
    'accounting': CalculatorSvg,
    'expenses': CalculatorSvg,
    'insurance': ShieldSvg,
    'crm': CRMSvg,
    'marketing': MarketingSvg,
    'superadmin': ClinicSvg,
    'settings': PlusSvg,
    'laboratories': LabSvg
  }
  return arr[props.module] || UserSvg
})
</script>

<style scoped lang="scss">
.btn-themed {
  svg {
    width: 16px;
    height: 16px;
  }
}
</style>
